import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import cn from 'classnames';
import Decimal from 'decimal.js';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, Select, Tooltip, Typography, useStateInRef } from 'seagull';
import { billingStagesTextsPath, billingTriggersTextsPath } from '~/business-logic/billingLogic';
import { AutomaticBillingStages } from '~/components/ServiceDetails/BillingInitiationControl/AutomaticBillingStages/AutomaticBillingStages';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { StagesTerms, serviceModel } from '~/models';
import { isFromAmendment } from '~/models/service/selectors';
import { resolveStageScheduleContext } from '~/models/service/stageScheduleContext';
import { fromDateStampObject, toDatestampString } from '~/models/time/translator';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { BillingStagesSelect } from '../BillingStagesSelect/BillingStagesSelect';
import { getBillingTriggerOptionsForFormState } from './BillingTrigger.utils';
import styles from './BillingTriggerDropdown.module.scss';
export default function BillingTriggerDropdown(props) {
    var _stagedBillingBehaviourOptions_find;
    const { serviceOrigin, service, stageClassName, stagesClassName, effectiveDate } = props;
    const { getNow } = useIoContext();
    const getProps = useStateInRef(props);
    const { control, watch, getValues, setValue } = useFormContext();
    const [recurrencePeriod, pricing, oneTimeInStagesBilling, price, discount] = watch([
        'recurrencePeriod',
        'pricing',
        'oneTimeInStagesBilling',
        'price',
        'discount'
    ]);
    const [triggerDropdownRef, setTriggerDropdownRef] = useState(null);
    const [stagedBillingBehaviourRef, setStagedBillingBehaviourRef] = useState(null);
    const featureFlags = useFeatureFlags();
    const { AUTOMATIC_STAGES } = featureFlags;
    const { t: billingTriggersTexts } = useLocaleKeys(billingTriggersTextsPath);
    const { t: billingStagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const [options, setOptions] = useState(()=>getBillingTriggerOptionsForFormState({
            t: billingTriggersTexts,
            data: getValues(),
            serviceOrigin,
            isExistingService: !!service,
            featureFlags
        }));
    const isApprovedServiceInAgreement = !!(serviceOrigin === 'agreement' && service && !serviceModel.isAwaitingApproval(service.status));
    useEffect(()=>{
        if (recurrencePeriod === 'onDemand') return;
        const { service, serviceOrigin } = getProps();
        const newOptions = getBillingTriggerOptionsForFormState({
            t: billingTriggersTexts,
            data: getValues(),
            serviceOrigin,
            isExistingService: !!service,
            featureFlags
        });
        setOptions(newOptions);
        const selectedOption = newOptions.find((opt)=>opt.value === getValues().trigger);
        const isSelectedInOptions = !!selectedOption;
        if (!isSelectedInOptions || selectedOption.disabled) {
            const firstNotDisabledOption = newOptions.find((opt)=>!opt.disabled);
            setValue('trigger', (firstNotDisabledOption || newOptions[0]).value);
        }
    }, [
        pricing,
        recurrencePeriod,
        featureFlags
    ]);
    const stagedBillingBehaviourOptions = [
        {
            value: StagesTerms.AutoFirst,
            text: billingStagesTexts.terms.autoFirst.text(),
            subtext: billingStagesTexts.terms.autoFirst.subtext({
                autoInvoiceIssued: billingStagesTexts.invoiceIssuedTiming.onAgreementApproval()
            })
        },
        {
            value: StagesTerms.AllManual,
            text: billingStagesTexts.terms.allManual.text(),
            subtext: billingStagesTexts.terms.allManual.subtext()
        }
    ];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            recurrencePeriod !== 'inStages' && recurrencePeriod !== 'onDemand' && /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "trigger",
                render: (renderProps)=>{
                    var _options_find;
                    return /*#__PURE__*/ _jsx(Tooltip, {
                        title: isApprovedServiceInAgreement ? billingTriggersTexts.tooltips.cannotModifyTrigger() : '',
                        children: /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                            mobile: {
                                renderAsCard: true,
                                displayValue: (_options_find = options.find((opt)=>opt.value === renderProps.field.value)) === null || _options_find === void 0 ? void 0 : _options_find.text
                            },
                            bi: {
                                name: 'billing_trigger'
                            },
                            collapseHelperText: false,
                            ref: setTriggerDropdownRef
                        }, connectControllerToTextField(renderProps)), {
                            disabled: isApprovedServiceInAgreement,
                            label: billingTriggersTexts.label(),
                            testId: "sb-trigger",
                            SelectProps: {
                                renderValue: (value)=>{
                                    const selected = options.find((opt)=>opt.value === value);
                                    return selected ? selected.text : '';
                                }
                            },
                            children: options.map((param, idx)=>{
                                let { value, text, subtext, disabled, disabledTooltip } = param;
                                return /*#__PURE__*/ _jsxs(MenuItem, {
                                    value: value,
                                    disabled: disabled,
                                    tooltipProps: disabledTooltip ? {
                                        title: disabledTooltip,
                                        placement: 'top'
                                    } : undefined,
                                    variant: "multiline",
                                    testId: "sb-trigger-opt-".concat(idx),
                                    style: {
                                        width: triggerDropdownRef === null || triggerDropdownRef === void 0 ? void 0 : triggerDropdownRef.clientWidth
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            weight: "normal",
                                            children: text
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            children: subtext
                                        })
                                    ]
                                }, value);
                            })
                        }))
                    });
                }
            }),
            !!oneTimeInStagesBilling && AUTOMATIC_STAGES && /*#__PURE__*/ _jsx("div", {
                className: cn(styles.stages, stagesClassName),
                children: /*#__PURE__*/ _jsx(AutomaticBillingStages, {
                    testId: "sb-billing-stages",
                    stages: oneTimeInStagesBilling.stages(),
                    valueType: oneTimeInStagesBilling.valueType(),
                    context: resolveStageScheduleContext({
                        isTemplate: serviceOrigin === 'template',
                        isAmendment: !!service && isFromAmendment(service),
                        effectiveDate: effectiveDate ? fromDateStampObject(effectiveDate) : null,
                        now: getNow()
                    }),
                    effectiveDate: effectiveDate ? toDatestampString(fromDateStampObject(effectiveDate)) : null,
                    today: toDatestampString(getNow()),
                    totalRate: new Decimal(price || 0),
                    disabled: isApprovedServiceInAgreement,
                    onStagesChange: (stages)=>setValue('oneTimeInStagesBilling', oneTimeInStagesBilling.setStages(stages), {
                            shouldDirty: true
                        }),
                    onValueTypeChange: (valueType)=>setValue('oneTimeInStagesBilling', oneTimeInStagesBilling.setValueType(valueType), {
                            shouldDirty: true
                        })
                })
            }),
            !!oneTimeInStagesBilling && !AUTOMATIC_STAGES && /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.stages, stagesClassName),
                children: [
                    /*#__PURE__*/ _jsx(Select, {
                        mobile: {
                            renderAsCard: true,
                            displayValue: (_stagedBillingBehaviourOptions_find = stagedBillingBehaviourOptions.find((opt)=>opt.value === oneTimeInStagesBilling.terms())) === null || _stagedBillingBehaviourOptions_find === void 0 ? void 0 : _stagedBillingBehaviourOptions_find.text
                        },
                        bi: {
                            name: 'staged_billing_behaviour'
                        },
                        collapseHelperText: false,
                        label: billingStagesTexts.stagedBillingBehaviour(),
                        autoFocus: true,
                        ref: setStagedBillingBehaviourRef,
                        value: oneTimeInStagesBilling.terms(),
                        testId: "staged-billing-behaviour",
                        SelectProps: {
                            renderValue: (value)=>{
                                const selected = stagedBillingBehaviourOptions.find((opt)=>opt.value === value);
                                return selected ? selected.text : '';
                            }
                        },
                        onChange: (e)=>{
                            setValue('oneTimeInStagesBilling', oneTimeInStagesBilling.setTerms(e.target.value), {
                                shouldDirty: true
                            });
                        },
                        children: stagedBillingBehaviourOptions.map((param, idx)=>{
                            let { value, text, subtext } = param;
                            return /*#__PURE__*/ _jsxs(MenuItem, {
                                value: value,
                                variant: "multiline",
                                testId: "staged-billing-behaviour-opt-".concat(idx),
                                style: {
                                    width: stagedBillingBehaviourRef === null || stagedBillingBehaviourRef === void 0 ? void 0 : stagedBillingBehaviourRef.clientWidth
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        weight: "normal",
                                        children: text
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: subtext
                                    })
                                ]
                            }, value);
                        })
                    }),
                    /*#__PURE__*/ _jsx(BillingStagesSelect, {
                        totalRate: new Decimal(price || 0),
                        effectiveDate: effectiveDate,
                        discount: new Decimal(discount.amount || 0),
                        stageClassName: stageClassName,
                        oneTimeInStagesBilling: oneTimeInStagesBilling,
                        setOneTimeInStagesBilling: (oneTimeInStagesBilling)=>{
                            setValue('oneTimeInStagesBilling', oneTimeInStagesBilling, {
                                shouldDirty: true
                            });
                        },
                        isAmendment: service ? isFromAmendment(service) : false
                    })
                ]
            })
        ]
    });
}
